import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import * as api from '../../api/educentialApi';
import {
  FileText,
  File,
  GraduationCap,
  Bell,
  ArrowRight,
  Upload,
  Grid,
  CheckCircle,
  Clock,
  TrendingUp,
  BookOpen,
  Globe,
  Sparkles,
  Award,
  Target,
  Calendar,
  Users,
  Briefcase,
  Plane
} from 'lucide-react';

// Icon mapping for dynamic icons from API
const iconMap = {
  fileText: FileText,
  upload: Upload,
  grid: Grid,
  checkCircle: CheckCircle,
  clock: Clock,
  trendingUp: TrendingUp,
  bookOpen: BookOpen,
  globe: Globe,
  award: Award,
  target: Target,
  calendar: Calendar,
  users: Users,
  briefcase: Briefcase,
  plane: Plane,
  graduationCap: GraduationCap,
  bell: Bell,
  file: File
};

const StudentDashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    fetchEnrolledCourses();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await api.getDashboard();
      setDashboardData(response.data);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchEnrolledCourses = async () => {
    try {
      const response = await api.getEnrolledCourses();
      if (response.success) {
        setEnrolledCourses(response.data.slice(0, 3)); // Show only 3 recent courses
      }
    } catch (err) {
      console.error('Error fetching enrolled courses:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6">
            <p className="text-red-800 font-medium">Error: {error}</p>
            <button
              onClick={fetchDashboardData}
              className="mt-3 px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { metrics, roleSpecificData, recentActivity, quickActions } = dashboardData || {};
  const studentData = roleSpecificData || {};

  // Transform Quick Actions to replace "Browse Services" with "Browse Courses"
  const transformedQuickActions = quickActions?.map(action => {
    if (action.label?.toLowerCase().includes('browse services') ||
        action.label?.toLowerCase().includes('services') ||
        action.description?.toLowerCase().includes('browse our services')) {
      return {
        ...action,
        label: 'Browse Courses',
        description: 'Explore our K-12 curriculum courses aligned with WAEC, NECO, and JAMB',
        actionUrl: '/courses/catalog',
        icon: 'bookOpen'
      };
    }
    return action;
  }) || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header with Greeting */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Welcome back, {user?.fullName || user?.email?.split('@')[0] || 'Student'}!
              </h1>
              <p className="text-gray-600 mt-1">Track your education journey and explore opportunities</p>
            </div>
          </div>
        </div>

        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            label="Applications in Progress"
            value={metrics?.applicationsInProgress || 0}
            icon={FileText}
            gradient="from-teal-500 to-teal-600"
          />
          <MetricCard
            label="Documents Pending"
            value={metrics?.documentsPending || 0}
            icon={File}
            gradient="from-amber-500 to-amber-600"
          />
          <MetricCard
            label="Courses Enrolled"
            value={enrolledCourses?.length || 0}
            icon={GraduationCap}
            gradient="from-emerald-500 to-emerald-600"
          />
          <MetricCard
            label="Notifications"
            value={metrics?.unreadNotifications || 0}
            icon={Bell}
            gradient="from-purple-500 to-purple-600"
          />
        </div>

        {/* Quick Actions */}
        {transformedQuickActions && transformedQuickActions.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <Target className="w-5 h-5 text-teal-600" />
              <span>Quick Actions</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {transformedQuickActions.map((action) => (
                <QuickActionCard key={action.id} action={action} />
              ))}
            </div>
          </div>
        )}

        {/* My Learning Section */}
        {enrolledCourses.length > 0 && (
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
                <BookOpen className="w-5 h-5 text-teal-600" />
                <span>My Learning</span>
              </h2>
              <Link to="/my-courses" className="text-teal-600 hover:text-teal-700 text-sm font-medium flex items-center space-x-1 transition-colors">
                <span>View All Courses</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {enrolledCourses.map((enrollment) => (
                <CourseCard key={enrollment.id} enrollment={enrollment} />
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Active Applications */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-teal-50 to-teal-100/50 px-6 py-4 border-b border-teal-100">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
                  <FileText className="w-5 h-5 text-teal-600" />
                  <span>Active Applications</span>
                </h2>
              </div>
            </div>
            <div className="p-6">
              {studentData.applications && studentData.applications.length > 0 ? (
                <div className="space-y-3">
                  {studentData.applications.slice(0, 5).map((app) => (
                    <div key={app.id} className="group border border-gray-200 rounded-lg p-4 hover:border-teal-200 hover:bg-teal-50/30 transition-all">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900 group-hover:text-teal-600 transition-colors">
                            {app.service.charAt(0).toUpperCase() + app.service.slice(1)} Application
                          </p>
                          <p className="text-sm text-gray-600 mt-1 flex items-center space-x-1">
                            <Globe className="w-3.5 h-3.5" />
                            <span>{app.country}</span>
                          </p>
                        </div>
                        <StatusBadge status={app.status} />
                      </div>
                      <p className="text-xs text-gray-500 mt-3 flex items-center space-x-1">
                        <Clock className="w-3.5 h-3.5" />
                        <span>Updated: {new Date(app.updatedAt).toLocaleDateString()}</span>
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-600 font-medium mb-2">No applications yet</p>
                  <p className="text-sm text-gray-500 mb-4">Start your journey to study abroad</p>
                  <Link to="/dashboard/application" className="inline-flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium">
                    <span>Start Application</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-purple-50 to-purple-100/50 px-6 py-4 border-b border-purple-100">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
                <TrendingUp className="w-5 h-5 text-purple-600" />
                <span>Recent Activity</span>
              </h2>
            </div>
            <div className="p-6">
              {recentActivity && recentActivity.filter(activity =>
                !activity.title?.toLowerCase().includes('service enrolled') &&
                !activity.description?.toLowerCase().includes('enrolled in') &&
                !activity.title?.toLowerCase().includes('enrolled in service')
              ).length > 0 ? (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {recentActivity
                    .filter(activity =>
                      !activity.title?.toLowerCase().includes('service enrolled') &&
                      !activity.description?.toLowerCase().includes('enrolled in') &&
                      !activity.title?.toLowerCase().includes('enrolled in service')
                    )
                    .slice(0, 10)
                    .map((activity, index) => (
                    <div key={activity.id} className="relative pl-6 pb-4 border-l-2 border-gray-200 last:border-0 last:pb-0">
                      <div className="absolute left-0 top-0 -translate-x-1/2 w-3 h-3 bg-teal-500 rounded-full border-2 border-white"></div>
                      <p className="font-medium text-gray-900">{activity.title}</p>
                      <p className="text-sm text-gray-600 mt-1">{activity.description}</p>
                      <p className="text-xs text-gray-500 mt-2 flex items-center space-x-1">
                        <Calendar className="w-3.5 h-3.5" />
                        <span>{new Date(activity.timestamp).toLocaleString()}</span>
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <TrendingUp className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-600">No recent activity</p>
                  <p className="text-sm text-gray-500 mt-1">Your activities will appear here</p>
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

// Metric Card Component with modern design
const MetricCard = ({ label, value, icon: Icon, gradient }) => {
  return (
    <div className="relative bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 p-6 border border-gray-100 overflow-hidden group">
      <div className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-5 transition-opacity duration-300 from-teal-400 to-teal-600"></div>
      <div className="relative flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm text-gray-600 font-medium mb-2">{label}</p>
          <p className="text-4xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`bg-gradient-to-br ${gradient} p-4 rounded-xl shadow-lg`}>
          <Icon className="w-7 h-7 text-white" />
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-400 to-teal-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
    </div>
  );
};

// Quick Action Card Component
const QuickActionCard = ({ action }) => {
  // Get icon component from map, default to CheckCircle
  const IconComponent = iconMap[action.icon] || CheckCircle;

  return (
    <Link
      to={action.actionUrl}
      className="group bg-white p-5 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 hover:border-teal-200"
    >
      <div className="flex items-start space-x-4">
        <div className="bg-teal-100 p-3 rounded-lg group-hover:bg-teal-600 transition-colors flex-shrink-0">
          <IconComponent className="w-6 h-6 text-teal-600 group-hover:text-white transition-colors" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 group-hover:text-teal-600 transition-colors mb-1">
            {action.label}
          </h3>
          <p className="text-sm text-gray-600 line-clamp-2">{action.description}</p>
        </div>
        <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-teal-600 group-hover:translate-x-1 transition-all flex-shrink-0" />
      </div>
    </Link>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    DRAFT: { bg: 'bg-gray-100', text: 'text-gray-800', icon: File },
    SUBMITTED: { bg: 'bg-blue-100', text: 'text-blue-800', icon: Upload },
    UNDER_REVIEW: { bg: 'bg-amber-100', text: 'text-amber-800', icon: Clock },
    OFFER_SECURED: { bg: 'bg-emerald-100', text: 'text-emerald-800', icon: CheckCircle },
    VISA_IN_PROGRESS: { bg: 'bg-purple-100', text: 'text-purple-800', icon: Plane },
    COMPLETED: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
    REJECTED: { bg: 'bg-red-100', text: 'text-red-800', icon: FileText },
  };

  const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', icon: File };
  const StatusIcon = config.icon;

  return (
    <span className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}>
      <StatusIcon className="w-3.5 h-3.5" />
      <span>{status?.replace(/_/g, ' ')}</span>
    </span>
  );
};

// Course Card Component for My Learning Section
const CourseCard = ({ enrollment }) => {
  const { Course } = enrollment;
  const progress = enrollment.progress || 0;

  return (
    <Link
      to={`/courses/${Course.id}/learn`}
      className="group bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 hover:border-teal-200 overflow-hidden"
    >
      {/* Course Thumbnail */}
      <div className="relative h-40 bg-gradient-to-br from-teal-400 to-teal-600 overflow-hidden">
        {Course.thumbnail ? (
          <img src={Course.thumbnail} alt={Course.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <BookOpen className="w-12 h-12 text-white opacity-50" />
          </div>
        )}
        {/* Progress Badge */}
        {progress > 0 && (
          <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full">
            <span className="text-xs font-semibold text-teal-600">{progress}% Complete</span>
          </div>
        )}
      </div>

      {/* Course Info */}
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 group-hover:text-teal-600 transition-colors mb-1 line-clamp-2">
          {Course.title}
        </h3>
        <p className="text-sm text-gray-600 mb-3 line-clamp-1">{Course.category}</p>

        {/* Progress Bar */}
        <div className="mb-3">
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-teal-600 transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Course Meta */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center space-x-1">
            <Clock className="w-3.5 h-3.5" />
            <span>{Course.duration}h</span>
          </div>
          <div className="flex items-center space-x-1">
            <Award className="w-3.5 h-3.5" />
            <span>{Course.level}</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default StudentDashboard;
